import { ReactNode } from 'react';
import { FaChevronLeft } from 'react-icons/fa';

import Loader from '../ui/loader/Loader';

import { useUser } from '@/hooks/useUser';

interface CollapsibleSidebarLayoutProps {
	sidebar: ReactNode;
	header: ReactNode;
	children: ReactNode;
	isSidebarOpen: boolean;
	toggleSidebar: () => void;
	contentBg?: string;
	useOverlay?: boolean;
}

const CollapsibleSidebarLayout = ({
	sidebar,
	header,
	children,
	isSidebarOpen,
	toggleSidebar,
	contentBg = 'bg-white',
	useOverlay = true,
}: CollapsibleSidebarLayoutProps) => {
	const { queryIsLoading } = useUser();
	if (queryIsLoading)
		return (
			<Loader className="w-full h-screen flex items-center justify-center" />
		);
	return (
		<div className="h-screen">
			<div className="flex">
				{/* Sidebar */}
				<div
					className={`h-screen top-0 left-0 z-50 md:z-30 transition-all duration-500 ease-in-out bg-white border-r
						${
							isSidebarOpen
								? 'w-60 opacity-100 pointer-events-auto'
								: 'w-0 opacity-0 pointer-events-none md:w-60 md:opacity-100 md:pointer-events-auto'
						} fixed md:sticky`}
				>
					<div
						className={`transition-opacity duration-500 ease-in-out ${
							isSidebarOpen ? 'opacity-100' : 'opacity-0 md:opacity-100'
						} h-full`}
					>
						{sidebar}
						<button
							onClick={toggleSidebar}
							className="md:hidden absolute top-20 -right-4 p-2 flex items-center justify-center bg-purpleBrand-500 text-white rounded-full shadow-md hover:bg-purpleBrand-600 transition-all duration-500"
						>
							<FaChevronLeft className="text-xs" />
						</button>
					</div>
				</div>

				{/* Overlay - only on mobile when sidebar is open */}
				{isSidebarOpen && useOverlay && (
					<div
						className="fixed inset-0 bg-black bg-opacity-30 z-40 md:hidden"
						onClick={toggleSidebar}
					></div>
				)}

				{/* Main Content */}
				<div
					className={`relative flex flex-1 flex-col overflow-x-hidden z-30 ${contentBg}`}
					id="pages"
				>
					{header}
					<main>
						<div className="mx-auto p-2 md:p-4">{children}</div>
					</main>
				</div>
			</div>
		</div>
	);
};

export default CollapsibleSidebarLayout;
